import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { API_URLS, API_CONFIG } from '../../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  ArrowLeft,
  Clock, 
  User, 
  Eye,
  Heart,
  Share2,
  Calendar,
  Tag,
  Facebook,
  Twitter,
  Linkedin,
  Copy,
  ChevronRight,
  Star,
  TrendingUp,
  Award,
  Lightbulb
} from 'lucide-react';
import ConsultationForm from '../ConsultationForm';
import DOMPurify from 'dompurify';

// Kategori ismini düzgün formata çevir (Türkçe karakter desteği)
const formatCategoryName = (categoryName) => {
  if (!categoryName) return 'Genel';
  
  // Önce lowercase yap, sonra kelimelere böl (tire veya boşlukla)
  const normalized = categoryName.toLowerCase();
  const words = normalized.split(/[-\s]+/);
  
  return words
    .map(word => {
      if (!word) return '';
      // Türkçe locale kullanarak baş harfi büyüt
      const firstChar = word.charAt(0);
      const rest = word.slice(1);
      // Türkçe locale ile büyük harfe çevir (i→İ, ı→I)
      const upperFirst = firstChar.toLocaleUpperCase('tr-TR');
      return upperFirst + rest;
    })
    .join(' ');
};

const SingleBlogPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blogPost, setBlogPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [showConsultModal, setShowConsultModal] = useState(false);
  const [contentReady, setContentReady] = useState(false);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Minimal HTML sanitizer to render trusted blog content safely
  const sanitizeHtml = (html) => {
    if (!html || typeof html !== 'string') return '';
    try {
      let cleaned = html;
      // Remove script/style tags and their content
      cleaned = cleaned.replace(/<\/(?:script|style)>/gi, '</removed>');
      cleaned = cleaned.replace(/<(?:script|style)[\s\S]*?<\/removed>/gi, '');
      // Remove on* inline event handlers (e.g., onclick)
      cleaned = cleaned.replace(/ on[a-zA-Z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '');
      // Disallow javascript: urls
      cleaned = cleaned.replace(/href\s*=\s*("|')\s*javascript:[^\1]*\1/gi, 'href="#"');
      // Final sanitize with DOMPurify
      return DOMPurify.sanitize(cleaned);
    } catch (_) {
      return DOMPurify.sanitize(html);
    }
  };

  const normalizeImageUrl = (url) => {
    if (!url) return '';
    const BASE_URL = API_CONFIG.BASE_URL;
    try {
      const u = String(url);
      // Rewrite getScreenshot style URLs to direct uploads (absolute or relative)
      if (u.includes('getScreenshot') && u.includes('file=')) {
        const match = u.match(/file=([^&]+)/);
        if (match && match[1]) {
          return `${BASE_URL}/uploads/${decodeURIComponent(match[1])}`;
        }
      }
      if (u.startsWith('http://') || u.startsWith('https://')) return u;
      // Normalize uploads paths
      if (u.startsWith('/backend/uploads/')) return `${BASE_URL}${u.replace('/backend', '')}`;
      if (u.startsWith('/uploads/')) return `${BASE_URL}${u}`;
      // If path looks like /blog/filename.jpg, map to uploads by filename
      if (u.startsWith('/blog/')) {
        const filename = u.split('/').pop();
        return `${BASE_URL}/uploads/${filename}`;
      }
      // Bare filename -> uploads
      if (!u.startsWith('/')) return `${BASE_URL}/uploads/${u}`;
      // Fallback: prefix base
      return `${BASE_URL}${u}`;
    } catch (_) {
      return url;
    }
  };

  useEffect(() => {
    const fetchBlogPost = async () => {
      setLoading(true);
      try {
        // Decide whether param is likely a UUID id or a slug; try slug first for slug-like
        const uuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
        let res;
        if (!uuidLike) {
          // Try slug first to avoid 404 noise
          res = await fetch(`${API_URLS.BLOG_DETAIL}&slug=${encodeURIComponent(id)}`);
          if (!res.ok) {
            const byId = await fetch(`${API_URLS.BLOG_DETAIL}&id=${encodeURIComponent(id)}`);
            if (!byId.ok) throw new Error('Failed');
            res = byId;
          }
        } else {
          // Try id first if it looks like a UUID
          res = await fetch(`${API_URLS.BLOG_DETAIL}&id=${encodeURIComponent(id)}`);
          if (!res.ok) {
            const bySlug = await fetch(`${API_URLS.BLOG_DETAIL}&slug=${encodeURIComponent(id)}`);
            if (!bySlug.ok) throw new Error('Failed');
            res = bySlug;
          }
        }
        const data = await res.json();
        const rawCategorySlug = data.category || 'genel';
        // Use category_name from API if available, otherwise format the slug
        const categoryName = data.category_name || formatCategoryName(rawCategorySlug);
        
        const post = {
          id: data.id || data.slug,
          title: data.title,
          content: data.content || '',
          category: categoryName,
          author: data.author || 'Editör',
          authorRole: data.author_role || '',
          authorBio: data.author_bio || '',
          publishDate: data.published_at || data.publishDate,
          updatedDate: data.updated_at || data.updatedDate,
          readTime: data.read_time || '5 dakika',
          views: Number(data.views || 0),
          likes: Number(data.likes || 0),
          category_slug: rawCategorySlug.toLowerCase().replace(/[^a-z0-9çğıöşü\-\s]/gi, '').replace(/\s+/g, '-'),
          tags: Array.isArray(data.tags) ? data.tags : [],
          featured_image: normalizeImageUrl(data.image || ''),
          image_focal_x: typeof data.image_focal_x === 'number' ? data.image_focal_x : 50,
          image_focal_y: typeof data.image_focal_y === 'number' ? data.image_focal_y : 50,
        };
        setBlogPost(post);
        setLikeCount(post.likes);
        
        // Fetch SEO data from pages_seo table
        const blogPath = `/blog/${data.slug || id}`;
        try {
          const seoRes = await fetch(`${API_CONFIG.BASE_URL}/backend/admin_api.php?action=getPageSeo&path=${encodeURIComponent(blogPath)}`);
          if (seoRes.ok) {
            const seoData = await seoRes.json();
            if (seoData.status === 'success' && seoData.data) {
              // Set SEO meta tags from pages_seo
              const metaTitle = seoData.data.title || post.title;
              document.title = metaTitle;
              
              // Ensure meta tags exist and update them
              const ensureMeta = (selector, attrs) => {
                let el = document.querySelector(selector);
                if (!el) {
                  el = document.createElement('meta');
                  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
                  document.head.appendChild(el);
                }
                return el;
              };
              
              // Description
              if (seoData.data.description) {
                const desc = ensureMeta('meta[name="description"]', { name: 'description' });
                desc.setAttribute('content', seoData.data.description);
              } else {
                // Remove if empty
                const desc = document.querySelector('meta[name="description"]');
                if (desc) desc.remove();
              }
              
              // Keywords
              if (seoData.data.keywords) {
                const kw = ensureMeta('meta[name="keywords"]', { name: 'keywords' });
                kw.setAttribute('content', seoData.data.keywords);
              } else {
                const kw = document.querySelector('meta[name="keywords"]');
                if (kw) kw.remove();
              }
              
              // OG Title
              const ogTitle = ensureMeta('meta[property="og:title"]', { property: 'og:title' });
              ogTitle.setAttribute('content', metaTitle);
              
              // OG Description
              if (seoData.data.description) {
                const ogDesc = ensureMeta('meta[property="og:description"]', { property: 'og:description' });
                ogDesc.setAttribute('content', seoData.data.description);
              } else {
                const ogDesc = document.querySelector('meta[property="og:description"]');
                if (ogDesc) ogDesc.remove();
              }
              
              // OG Image
              const ogImageUrl = seoData.data.og_image || post.featured_image;
              if (ogImageUrl) {
                const ogImage = ensureMeta('meta[property="og:image"]', { property: 'og:image' });
                const fullImageUrl = ogImageUrl.startsWith('http') ? ogImageUrl : `${API_CONFIG.BASE_URL}${ogImageUrl.startsWith('/') ? ogImageUrl : '/' + ogImageUrl}`;
                ogImage.setAttribute('content', fullImageUrl);
              } else {
                const ogImage = document.querySelector('meta[property="og:image"]');
                if (ogImage) ogImage.remove();
              }
            } else {
              // No SEO data, use defaults
              document.title = post.title;
            }
          } else {
            // Fallback to default title
            document.title = post.title;
          }
        } catch (seoError) {
          console.error('Error fetching SEO data:', seoError);
          // Fallback to default title
          document.title = post.title;
        }
        
        // Fetch related posts: strictly same category, exclude current
        try {
          const relRes = await fetch(`${API_URLS.BLOGS}&category=${encodeURIComponent(post.category)}`);
          if (relRes.ok) {
            const relData = await relRes.json();
            const items = Array.isArray(relData) ? relData : relData?.data || [];
            const related = items
              .filter(b => (b.id || b.slug) !== post.id)
              .slice(0, 5)
              .map(b => ({
                id: b.id || b.slug,
                slug: b.slug || b.id,
                title: b.title,
                category: b.category_name || b.category || 'Genel',
                readTime: b.read_time || b.readTime || '5 dakika',
              }));
            setRelatedPosts(related);
          }
        } catch (_) {}
        
        // Check like status after blog post is loaded
        setTimeout(() => {
          
          checkLikeStatus(post.id);
        }, 100);

        // Track unique device for this blog and update views count
        try {
          const deviceId = getSessionId();
          fetch(`${API_CONFIG.BASE_URL}/backend/api.php?action=trackBlogDevice&id=${post.id}&device_id=${encodeURIComponent(deviceId)}`)
            .then(res => res.json())
            .then(data => {
              // Update views count if returned from API
              if (data.success && typeof data.views === 'number') {
                setBlogPost(prev => prev ? { ...prev, views: data.views } : prev);
              }
            })
            .catch(() => {});
        } catch (_) {}
      } catch (error) {
        console.error('Error fetching blog post:', error);
        navigate('/blog');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchBlogPost();
    }
  }, [id, navigate]);

  // Generate or get session ID
  const getSessionId = () => {
    let sessionId = localStorage.getItem('blog_session_id');
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('blog_session_id', sessionId);
    }
    return sessionId;
  };

  const handleLike = async () => {
    if (!blogPost) return;
    
    try {
      const sessionId = getSessionId();
      const response = await fetch(`${API_CONFIG.BASE_URL}/backend/api.php?action=likeBlog&id=${blogPost.id}&session_id=${sessionId}`);
      
      if (!response.ok) {
        throw new Error('Failed to like blog');
      }
      
      const data = await response.json();
      
      if (data.status === 'liked') {
        setLiked(true);
        if (data.like_count !== undefined) {
          setLikeCount(data.like_count);
        }
      } else if (data.status === 'unliked') {
        setLiked(false);
        if (data.like_count !== undefined) {
          setLikeCount(data.like_count);
        }
      }
    } catch (error) {
      console.error('Error liking blog:', error);
      alert('Beğeni işlemi başarısız oldu. Lütfen tekrar deneyin.');
    }
  };

  const checkLikeStatus = async (blogId) => {
    if (!blogId) return;
    
    try {
      const sessionId = getSessionId();
      
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/backend/api.php?action=checkBlogLike&id=${blogId}&session_id=${sessionId}`);
      
      if (response.ok) {
        const data = await response.json();
        
        setLiked(data.liked);
      } else {
        console.error('Failed to check like status:', response.status);
      }
    } catch (error) {
      console.error('Error checking like status:', error);
    }
  };


  const shareUrl = window.location.href;
  const shareTitle = blogPost?.title || '';

  const shareOnSocial = (platform) => {
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    };
    
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      alert('Link kopyalandı!');
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="rounded-full h-32 w-32 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!blogPost) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Blog yazısı bulunamadı</h1>
          <Link to="/blog">
            <Button className="bg-purple-600 hover:bg-purple-700">
              Blog sayfasına dön
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div 
        className="bg-white border-b"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-purple-600">Ana Sayfa</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/blog" className="hover:text-purple-600">Blog</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to={`/blog?category=${encodeURIComponent(blogPost.category_slug)}`} className="hover:text-purple-600">
              {blogPost.category}
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900 truncate max-w-xs">{blogPost.title}</span>
          </div>
        </div>
      </div>

      <div 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Back Button */}
            <Link to="/blog">
              <Button variant="outline" className="mb-8 hover:bg-purple-50">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Blog sayfasına dön
              </Button>
            </Link>

            {/* Article Header */}
            <article className="bg-white rounded-xl shadow-lg overflow-hidden">
              {/* Featured Image */}
              <div className="relative h-64 md:h-96 bg-gradient-to-br from-purple-600 to-blue-600">
                <div className="absolute inset-0 bg-black/30 z-10"></div>
                {/* Featured image if available */}
                {blogPost.featured_image && (
                  <img
                    src={blogPost.featured_image}
                    alt={blogPost.title}
                    className="absolute inset-0 w-full h-full object-cover object-top z-0"
                    style={{ objectPosition: '50% 0%' }}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                )}
                <div className="absolute top-6 left-6 z-20">
                  <Badge className="bg-white text-gray-900">
                    {blogPost.category}
                  </Badge>
                </div>
                <div className="absolute bottom-6 left-6 right-6 text-white z-20">
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4">
                    {blogPost.title}
                  </h1>
                </div>
              </div>

              {/* Article Meta */}
              <div className="p-6 border-b">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
                  {/* Author Info */}
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{blogPost.author}</h3>
                      <p className="text-sm text-purple-600">{blogPost.authorRole}</p>
                    </div>
                  </div>

                  {/* Article Stats */}
                  <div className="flex items-center space-x-6 text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(blogPost.publishDate).toLocaleDateString('tr-TR')}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>{blogPost.readTime}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Eye className="w-4 h-4" />
                      <span>{blogPost.views.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {Array.isArray(blogPost.tags) && blogPost.tags.map((tag, index) => (
                    <Badge key={index} variant="secondary" className="cursor-pointer hover:bg-purple-100">
                      <Tag className="w-3 h-3 mr-1" />
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Social Share */}
              <div className="px-6 py-4 bg-gray-50 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <Button
                      onClick={handleLike}
                      variant="outline"
                      className={`${liked ? 'bg-red-50 border-red-200 text-red-600' : ''}`}
                    >
                      <Heart className={`w-4 h-4 mr-2 ${liked ? 'fill-red-600' : 'fill-none'}`} />
                      {likeCount}
                    </Button>
                    
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">Paylaş:</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => shareOnSocial('facebook')}
                        className="text-blue-600 hover:bg-blue-50"
                      >
                        <Facebook className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => shareOnSocial('twitter')}
                        className="text-blue-400 hover:bg-blue-50"
                      >
                        <Twitter className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => shareOnSocial('linkedin')}
                        className="text-blue-700 hover:bg-blue-50"
                      >
                        <Linkedin className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyToClipboard}
                        className="hover:bg-gray-100"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Article Content */}
              <div className="p-6 lg:p-12">
                <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(blogPost.content) }} />
              </div>

              
            </article>

            
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Related Posts */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  <span>İlgili Makaleler</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {relatedPosts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug || post.id}`}
                    className="flex space-x-3 group cursor-pointer"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex-shrink-0"></div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900 group-hover:text-purple-600 transition-colors line-clamp-2 mb-2">
                        {post.title}
                      </h4>
                      <div className="flex items-center text-xs text-gray-500">
                        <Badge variant="secondary" className="mr-2 text-xs">
                          {post.category}
                        </Badge>
                        <Clock className="w-3 h-3 mr-1" />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </CardContent>
            </Card>

            {/* ProWork Services */}
            <Card className="shadow-lg bg-gradient-to-br from-purple-600 to-blue-600 text-white">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2">Profesyonel Destek</h3>
                <p className="text-purple-100 text-sm mb-6">
                  Bu makale konusunda uzman desteği almak mı istiyorsunuz?
                </p>
                <Button onClick={() => setShowConsultModal(true)} className="w-full bg-white text-purple-600 hover:bg-gray-100">
                  <Lightbulb className="w-4 h-4 mr-2" />
                  Ücretli Danışmanlık
                </Button>
                <p className="text-xs text-purple-200 mt-4">
                  🎯 1000+ başarılı proje • ⭐ 4.9 müşteri puanı
                </p>
              </CardContent>
            </Card>
            {/* Consultation Modal */}
            {showConsultModal && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur z-50 flex items-center justify-center p-4">
                <div className="relative max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                  <ConsultationForm isModal={true} onClose={() => setShowConsultModal(false)} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Support CTA: Aradığınız Cevabı Bulamadınız mı? */}
      <div 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl shadow-lg">
          <div className="p-8 text-center space-y-6">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-2">Aradığınız Cevabı Bulamadınız mı?</h3>
              <p className="text-purple-100">Uzman ekibimiz 7/24 yardımcı olmak için hazır. Ortalama yanıt süremiz 5 dakika!</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/iletisim" className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-medium inline-block text-center transition-colors">
                İletişim
              </Link>
              <button type="button" onClick={() => window.open('https://api.whatsapp.com/send/?phone=905324404011&text=Merhaba%2C%20website-al%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum.', '_blank')} className="border border-white text-white hover:bg-white/10 px-8 py-3 rounded-lg font-medium inline-flex items-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                WhatsApp İletişim
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default SingleBlogPage; 